import { WarningCircleIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import type { ExtensionColumnMetadata } from '@erp/shared-types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type IssueCode = ExtensionColumnMetadata['issues'][number]['code'];

/** Header marker for a configured extension column the server could not fully serve. */
export function ExtensionColumnIssues({
  metadata,
}: {
  metadata?: ExtensionColumnMetadata;
}) {
  const gt = useGT();
  const codes = [...new Set(metadata?.issues.map((issue) => issue.code))];
  if (codes.length === 0) return null;
  const messages: Record<IssueCode, string> = {
    unavailable: gt(
      'This field is no longer available. Remove it in column settings.',
      { context: 'extension column issue: field deleted or not provisioned' },
    ),
    not_exposed: gt('This field is not exposed for table columns.', {
      context: 'extension column issue: columns exposure switched off',
    }),
    unsupported_relation: gt('This related-record column is not supported.', {
      context: 'extension column issue: relation not supported',
    }),
    invalid_value: gt('Some stored values are invalid and show as empty.', {
      context: 'extension column issue: stored value failed validation',
    }),
  };
  const text = codes.map((code) => messages[code]).join(' ');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <WarningCircleIcon
          className="size-4 shrink-0 text-amber-600"
          aria-label={text}
          role="img"
        />
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}
